/**
=========================================================
* Argon Dashboard 2 MUI - v3.0.1
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-material-ui
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components

import Card from "@mui/material/Card";

// Argon Dashboard 2 MUI components
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonButton from "components/ArgonButton";

// Argon Dashboard 2 MUI examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";
import Grid from "@mui/material/Grid";
import AppBar from "@mui/material/AppBar";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";

import breakpoints from "assets/theme/base/breakpoints";

// Data
// import authorsTableData from "layouts/tables/data/authorsTableData";
//import ParentsTableData from "Views/people/parents/data/parentsTableData";
import projectsTableData from "layouts/tables/data/projectsTableData";
// import ClassMgtTableData from "Views/Activities/ClassMgt/data/classMgtTableData";

import tutorsParollTableData from "Views/Activities/TutorsPayroll/data/tutorsPayrollTableData";

// import DraftTutorePayrollTableData from "Views/FinanceManagment/DraftTutorSalary/data/draftTutorSalaryTableData";

// import supportTicketTableData from "Views/Activities/SupportTicket/data/supportTicketTableData";

import AllTransactionTableData from "Views/FinanceManagment/AllTransaction/data/AllTransactionTableData";

import { useState, useEffect } from "react";
import ArgonInput from "components/ArgonInput";
import Dropdown from "components/CustomDropdown/Dropdown";
import dayjs from "dayjs";
import CustomDatePicker from "components/CustomDatePicker/CustomDatePicker";
import PaginationBar from "components/PaginationBar/PaginationBar";

function Tables() {
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [mode, setMode] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filters, setFilters] = useState({});

  const { columns, rows, loading, fetchTransactions } = AllTransactionTableData(filters);

  // Pagination
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedRows = rows.slice(startIndex, endIndex);

  // Update sno for paginated rows
  const rowsWithCorrectSno = paginatedRows.map((row, index) => ({
    ...row,
    sno: (
      <ArgonTypography variant="caption" color="text" fontWeight="medium">
        {startIndex + index + 1}
      </ArgonTypography>
    ),
  }));

  useEffect(() => {
    setFilters({
      fromDate,
      toDate,
      method: mode,
    });
    setCurrentPage(1); // Reset to first page when filters change
  }, [fromDate, toDate, mode]);

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(event.target.value);
    setCurrentPage(1); // Reset to first page when changing rows per page
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <ArgonBox py={3}>
        <ArgonBox mb={3}>
          <Card>
            <ArgonBox p={3}>
              <ArgonTypography mb={2} variant="h6" fontWeight="medium">
                All Transaction
              </ArgonTypography>

              <ArgonBox
                display="flex"
                gap={2}
                alignItems="center"
                justifyContent="space-between"
                mb={2}
              >
                <ArgonBox display="flex" alignItems="center" gap={2}>
                  <CustomDatePicker
                    label="From"
                    value={fromDate}
                    onChange={(newValue) => setFromDate(newValue)}
                    width={160}
                    maxDate={dayjs()}
                  />
                  <CustomDatePicker
                    label="To"
                    value={toDate}
                    onChange={(newValue) => setToDate(newValue)}
                    width={160}
                    maxDate={dayjs()}
                    minDate={fromDate}
                  />
                </ArgonBox>
                <ArgonBox display="flex" alignItems="center" gap={2}>
                  <Dropdown
                    label="Mode"
                    value={mode}
                    options={["CASH", "ONLINE"]}
                    onChange={(e) => setMode(e.target.value)}
                    width={150}
                  />
                </ArgonBox>
              </ArgonBox>
            </ArgonBox>

            <ArgonBox
              sx={{
                "& .MuiTableRow-root:not(:last-child)": {
                  "& td": {
                    borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                      `${borderWidth[1]} solid ${borderColor}`,
                  },
                },
                // minHeight: "400px", // Fixed height to prevent layout shift
              }}
            >
              {loading ? (
                <ArgonBox p={3} textAlign="center">
                  <ArgonTypography variant="body2">Loading...</ArgonTypography>
                </ArgonBox>
              ) : rows.length === 0 ? (
                <ArgonBox textAlign="center" py={4}>
                  <ArgonTypography variant="h6" color="text" fontWeight="medium">
                    No Transaction found
                  </ArgonTypography>
                  <ArgonTypography variant="caption" color="secondary">
                    Try adjusting your filters or check back later.
                  </ArgonTypography>
                </ArgonBox>
              ) : (
                <Table columns={columns} rows={rowsWithCorrectSno} />
              )}

              {/* Pagination */}
              {rows.length > 0 && (
                <PaginationBar
                  currentPage={currentPage}
                  rowsPerPage={rowsPerPage}
                  totalItems={rows.length}
                  onPageChange={handlePageChange}
                  onRowsPerPageChange={handleRowsPerPageChange}
                />
              )}
            </ArgonBox>
          </Card>
        </ArgonBox>
      </ArgonBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Tables;
